import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import localforage from 'localforage';
import _ from 'lodash';

// React bootstrap components
import { Navbar, Container, Nav, NavDropdown } from "react-bootstrap";
import { Button, TextField, Switch, FormControl, FormLabel, FormGroup, FormControlLabel, Checkbox } from '@mui/material';

// Material UI components

// Components
import Separator from '../../../Separator/Separator';

// APIs
import { firmwareApi } from '../../../../api/firmwareApi';

// Types and constants
import { DATE_TIME_FORMAT } from '../../../../utils/dateTimeFormat';
import { FirmwareModel } from '../../../../shared/types';
import { ROLES, RoleSelection } from '../../../../shared/constants';
import AssignUserDialog from '../../../AssignUserDialog/AssignUserDialog';
import ErrorDialog from '../../../ErrorDialog/ErrorDialog';

type FirmwareDetailsParams = {
    firmwareId: string;
}

const FirmwareDetails: FunctionComponent = (props: any): ReactElement => {
    const [firmwareToEdit, setFirmwareToEdit] = useState<FirmwareModel | null>(null);
    const [originalFirmwareToEdit, setOriginalFirmwareToEdit] = useState<FirmwareModel | null>(null);
    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [enableSaveBtn, setEnableSaveBtn] = useState<boolean>(false);
    const [roleSelections, setRoleSelections] = useState<RoleSelection[]>([]);
    const [openAssignUserDialog, setOpenAssignUserDialog] = useState<boolean>(false);
    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false);
    const [error, setError] = useState<any>();

    const params = useParams<FirmwareDetailsParams>();
    const firmwareId = params.firmwareId;

    const navigate = useNavigate();
    const goTo = (route: string) => {
        navigate(route);
    }

    const loadFirmwareToEdit = async () => {
        if (firmwareId && firmwareId !== 'new') {
            const firmwares = await localforage.getItem('firmwares') as FirmwareModel[];
            const foundFirmware = firmwares.find((fw) => fw.id === firmwareId);
            if (foundFirmware) {
                setFirmwareToEdit(foundFirmware);
                setOriginalFirmwareToEdit(foundFirmware);
            }
        } else {
            setFirmwareToEdit({} as FirmwareModel);
        }
    }

    const roleSelectionHandler = (e: any) => {
        const { name, value, checked } = e.target;
        const foundIndex = roleSelections.findIndex((item) => item.name === name);
        if (foundIndex >= 0) {
            const newRoleSelctions = _.cloneDeep(roleSelections);
            newRoleSelctions[foundIndex].selected = checked;
            setRoleSelections(newRoleSelctions);

            // get selected roles name only
            const selectedRoles = newRoleSelctions.filter((item) => item.selected).map((item) => item.name);
            if (firmwareToEdit) {
                const newFirmwareToEdit = { ...firmwareToEdit, authorizedRoles: selectedRoles };
                if (!_.isEqual(originalFirmwareToEdit?.authorizedRoles, newFirmwareToEdit.authorizedRoles)) {
                    setEnableSaveBtn(true);
                } else {
                    setEnableSaveBtn(false);
                }
                setFirmwareToEdit(newFirmwareToEdit);
            }
        }
    }

    const inputChangeHandler = (e: any) => {
        const { name, value, checked } = e.target;
        let newFirmwareToEdit: FirmwareModel;

        if (name === 'status') {
            newFirmwareToEdit = firmwareToEdit ? { ...firmwareToEdit, status: checked } : {} as FirmwareModel;
            if (!_.isEqual(originalFirmwareToEdit, newFirmwareToEdit)) {
                const { componentId, version, protocolVersion } = newFirmwareToEdit;
                setEnableSaveBtn(!!componentId && !!version && !!protocolVersion);
            } else {
                setEnableSaveBtn(false);
            }
        } else {
            newFirmwareToEdit = firmwareToEdit ? { ...firmwareToEdit, [name]: value } : {} as FirmwareModel;
            const { componentId, version, protocolVersion } = newFirmwareToEdit;
            setEnableSaveBtn(!!componentId && !!version && !!protocolVersion);
        }

        if (newFirmwareToEdit) {
            setFirmwareToEdit(newFirmwareToEdit);
        }
    }

    const changeHandler = (name: string, value: any) => {
        if (firmwareToEdit) {
            setFirmwareToEdit({
                ...firmwareToEdit,
                [name]: value
            });
        }
    }

    const saveFirmware = async () => {
        try {
            // If firmware image is uploaded then create a new version of a firmware otherwise update the existing one. 
            if (selectedFile && firmwareToEdit) {
                const formData = new FormData();
                formData.append('firmwareblob', selectedFile, selectedFile.name);
                await firmwareApi.createFirmware(formData, firmwareToEdit);
                setEnableSaveBtn(false);
            } else if (firmwareToEdit) {
                await firmwareApi.updateFirmware(firmwareToEdit);
                setEnableSaveBtn(false);
            }
            goTo('/firmware');
        } catch (error) {
            setOpenErrorDialog(true);
            setError(error);
        }
    }

    const fileChangeHandler = async (event: any) => {
        setSelectedFile(event.target.files[0]);
    };

    const handleClose = () => {
        setOpenAssignUserDialog(false);
        setOpenErrorDialog(false);
    };

    const manageUsers = () => {
        setOpenAssignUserDialog(true);
    }

    const authorizedUsersHandler = (userIds: string[]) => {
        if (firmwareToEdit) {
            const uniqueIds = new Set([...firmwareToEdit.authorizedUsers, ...userIds]);
            const newFirmwareToEdit = { ...firmwareToEdit, authorizedUsers: Array.from(uniqueIds) };

            if (!_.isEqual(originalFirmwareToEdit?.authorizedUsers, newFirmwareToEdit.authorizedUsers)) {
                setEnableSaveBtn(true);
            } else {
                setEnableSaveBtn(false);
            }
            setFirmwareToEdit(newFirmwareToEdit);
        }
        setOpenAssignUserDialog(false);
    }

    useEffect(() => {
        loadFirmwareToEdit();
    }, []);

    useEffect(() => {
        const roles = ROLES.map((r) => ({ name: r, selected: false }));
        firmwareToEdit?.authorizedRoles?.forEach((item) => {
            const foundIndex = roles.findIndex((x) => x.name === item);
            if (foundIndex >= 0) {
                roles[foundIndex].selected = true;
            }
        });
        setRoleSelections(roles);
    }, [firmwareToEdit])

    if (!firmwareToEdit) return <p>Loading...</p>;

    return (
        <div className='firmware-details-container' data-testid='firmware-details-container' >
            <Navbar bg="light" expand="lg">
                <Container fluid>
                    <Navbar.Brand><b>{firmwareToEdit.componentId}</b></Navbar.Brand>
                    <Navbar.Toggle aria-controls="navbarScroll" />
                    <Navbar.Collapse id="navbarScroll">
                        <Nav
                            className="me-auto my-2 my-lg-0"
                            style={{ maxHeight: '100px' }}
                            navbarScroll
                        >
                            <NavDropdown title="Current" id="navbarScrollingDropdown">
                                <NavDropdown.Item onClick={() => changeHandler('version', '0.0.1')} >0.0.1</NavDropdown.Item>
                                <NavDropdown.Item onClick={() => changeHandler('version', '0.0.1')}>0.0.2</NavDropdown.Item>
                                <NavDropdown.Divider />
                                <NavDropdown.Item >Current</NavDropdown.Item>
                            </NavDropdown>
                        </Nav>
                        <Button
                            data-testid='save-firmware-btn'
                            variant='contained'
                            color='success'
                            onClick={saveFirmware}
                            disabled={!enableSaveBtn}
                        >Save
                        </Button>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
            <Separator />
            <div className='firmware-edit-form'>
                <div className='form-inputs-container'>
                    <div>
                        <TextField
                            label='Name'
                            id='name'
                            name='name'
                            value={firmwareToEdit.name}
                            onChange={inputChangeHandler}
                            helperText='Field is limited to 32 characters'
                            margin='normal'
                            fullWidth
                            required
                            inputProps={{ 'data-testid': 'firmware-name-input' }}
                        />
                        <Separator />
                    </div>
                    <div>
                        <TextField
                            label='Component Id'
                            id="outlined-required"
                            name='componentId'
                            value={firmwareToEdit.componentId}
                            onChange={inputChangeHandler}
                            margin='normal'
                            fullWidth
                            required
                            inputProps={{ 'data-testid': 'firmware-componentId-input' }}
                        />
                        <Separator />
                    </div>
                    {
                        firmwareId === 'new' && (
                            <div>
                                <TextField
                                    label='Firmware Image'
                                    type='file'
                                    name='firmware'
                                    id='firmwareFile'
                                    onChange={fileChangeHandler}
                                    helperText='Upload firmware image'
                                    margin='normal'
                                    fullWidth
                                    required
                                    inputProps={{ 'data-testid': 'firmware-file-input' }}
                                />
                                <Separator />
                            </div>
                        )
                    }
                    <div>
                        <TextField
                            label='App Version'
                            id='appVersion'
                            name='appVersion'
                            value={firmwareToEdit.appVersion}
                            onChange={inputChangeHandler}
                            margin='normal'
                            fullWidth
                        />
                        <Separator />
                    </div>
                    <div>
                        <TextField
                            label='Firmware Version'
                            id='version'
                            name='version'
                            value={firmwareToEdit.version}
                            onChange={inputChangeHandler}
                            margin='normal'
                            fullWidth
                            required
                            helperText='The version of this image'
                            inputProps={{ 'data-testid': 'firmware-version-input' }}
                        />
                        <Separator />
                    </div>
                    <div>
                        <TextField
                            label='Protocol Version'
                            id='protocolVersion'
                            name='protocolVersion'
                            value={firmwareToEdit.protocolVersion}
                            onChange={inputChangeHandler}
                            margin='normal'
                            fullWidth
                            required
                            helperText='Protocol version this image adhers to'
                            inputProps={{ 'data-testid': 'firmware-protocol-version-input' }}
                        />
                        <Separator />
                    </div>
                    <div>
                        <TextField
                            label='Configuration'
                            id='configuration'
                            name='configuration'
                            value={firmwareToEdit.configuration}
                            onChange={inputChangeHandler}
                            margin='normal'
                            required
                            fullWidth
                            helperText='Enter configuration for the controller for which this will be associated with. If left empty it will be avaliable to all controllers'
                        />
                        <Separator />
                    </div>
                    <div>
                        <TextField
                            label='Notes'
                            id='notes'
                            name='description'
                            value={firmwareToEdit.description}
                            onChange={inputChangeHandler}
                            margin='normal'
                            fullWidth
                            helperText='Notes about the firmware'
                            multiline
                            minRows={3}
                        />
                        <Separator />
                    </div>
                </div>
                <div>
                    <p>Enabled:
                        <Switch
                            checked={firmwareToEdit.status}
                            color='success'
                            name='status'
                            value={firmwareToEdit.status}
                            onChange={inputChangeHandler}
                            data-testid='firmware-status'
                        />
                    </p>

                    <div className='create-update-dates'>
                        <p><span>Created at:</span> <span> {DATE_TIME_FORMAT.am_pm(firmwareToEdit.createdAt)} </span></p>
                        <p><span>Updated at:</span> <span> {DATE_TIME_FORMAT.am_pm(firmwareToEdit.modifiedAt)} </span></p>
                        <TextField
                            label='Authorized Users'
                            id='authorized-users'
                            name='authorizedUsers'
                            value={firmwareToEdit.authorizedUserNames}
                            onChange={inputChangeHandler}
                            onClick={manageUsers}
                            margin='normal'
                            fullWidth
                            multiline
                            minRows={1}
                            helperText='Enter authorized users to access this Firmware.'
                        />

                        <FormControl sx={{ m: 3 }} component="fieldset" variant="standard">
                            <FormLabel component="legend">Authorized Roles</FormLabel>
                            <FormGroup>
                                {
                                    roleSelections.map((role) => (
                                        <FormControlLabel
                                            control={
                                                (
                                                    <Checkbox
                                                        checked={role.selected}
                                                        onChange={roleSelectionHandler}
                                                        name={role.name}
                                                    />
                                                )
                                            }
                                            label={role.name}
                                        />
                                    ))
                                }
                            </FormGroup>
                        </FormControl>
                    </div>
                    <Separator />
                </div>

                {
                    openAssignUserDialog && (
                        <AssignUserDialog
                            openAssignUserDialog={openAssignUserDialog}
                            handleClose={handleClose}
                            authorizedUsersHandler={authorizedUsersHandler}
                        />
                    )
                }

                {
                    error &&
                    (
                        <ErrorDialog
                            openErrorDialog={openErrorDialog}
                            handleClose={handleClose}
                            error={error}
                        />
                    )
                }
            </div>
        </div>
    );
};

export default FirmwareDetails;
